class CollapsibleHeader extends Collapsible {
  constructor() {
    super();
    this.header = this.closest("[data-header]");
    if (this.header) {
      this.inverse = !!this.header.classList.contains("header--inverse");
      this.shadow = this.header.querySelector(".header-shadow");

      // extra events
      this.shadow?.addEventListener("click", this.closeAll.bind(this));
    }
  }

  construct() {
    // Init elements
    this.groups = this.hasAttribute("data-collapsible-group")
      ? [this.closest("[data-collapsible-group]")]
      : this.querySelectorAll("[data-collapsible-group]");
    this.triggers = this.querySelectorAll("[data-collapsible-trigger]");

    // Init events

    // shadow events
    this.shadow?.addEventListener("click", this.closeAll.bind(this));

    // Trigger events on [data-collapsible-trigger]
    this.triggers.forEach((trigger) => {
      trigger.removeEventListener("click", this.reducer.clickTarget);
      trigger.addEventListener("click", this.reducer.clickTarget);

      // Mouse enter
      if (this.options.onHover) {
        trigger.removeEventListener(
          "mouseenter",
          this.reducer.mouseEnterTarget
        );
        trigger.addEventListener("mouseenter", this.reducer.mouseEnterTarget);
      }
    });

    // Trigger mouseleave on container <collapsible-element>
    if (this.options.closeOnMouseleave) {
      this.removeEventListener("mouseleave", this.reducer.mouseLeaveContainer);
      this.addEventListener("mouseleave", this.reducer.mouseLeaveContainer);
    }
  }

  events() {
    if (!this.header) {
      super.events();
    }
  }

  open(group) {
    super.open(group);
    if (document.body) {
      document.body.classList.add("header-is-open");
    }
    this.toggleHeaderClass(true);
  }

  close(group) {
    super.close(group);
    if (document.body) {
      document.body.classList.remove("header-is-open");
    }
    this.toggleHeaderClass(false);
  }

  closeSiblings(currentGroup = null) {
    super.closeSiblings(currentGroup);
    this.toggleHeaderClass(false);
  }

  openAll() {
    super.openAll();
    this.toggleHeaderClass(true);
    if (document.body) {
      document.body.classList.add("header-is-open");
    }
  }

  closeAll() {
    super.closeAll();
    this.toggleHeaderClass(false);
    if (document.body) {
      document.body.classList.remove("header-is-open");
    }
  }

  toggleHeaderClass(open = true) {
    if (!this.header) {
      return false;
    }

    // Set header open
    if (open) {
      document.body.classList.add("header-is-open");
      this.inverse && this.header.classList.add("header--inverse-solid");
    } else {
      // set header closed if all menu items are closed
      let close = true;
      this.groups.forEach((group) => {
        if (group.classList.contains(this.options.toggleClass)) {
          close = false;
        }
      });
      if (close) {
        document.body.classList.remove("header-is-open");
        this.inverse && this.header.classList.remove("header--inverse-solid");
      }
    }
  }
}

class CollapsibleDrawer extends Collapsible {
  constructor() {
    super();
  }

  open(group) {
    super.open(group);
    // scroll to the top of the new collapsible element and remove the scroll of the parent elements to prevent extra unnecessary scroll
    group.closest('[data-collapsible-level]').scrollTop = 0;
    group.closest('[data-collapsible-level]').style.overflow = 'hidden';
    group.closest('[data-collapsible-level="1"]').style.overflow = 'hidden';
  }

  close(group) {
    super.close(group);
    // reset the scroll style when closing collapsibles
    const collapsibleLevelElem = group.closest('[data-collapsible-level]');
    collapsibleLevelElem.style.overflow = null;
    // set the level 1 always to overflow hidden, unless it's the one visible
    const isGroupOneOpen = this.querySelector('[data-collapsible-group="1"]').classList.contains(
      this.options.toggleClass
    );
    if (isGroupOneOpen) {
      this.querySelector('[data-collapsible-level]').style.overflow = 'hidden';
    }
  }
}

class ToggleMobileNavigation extends ToggleElementClass {
  constructor() {
    super();

    this.header = this.closest("[data-header]");
    this.headerWrapper = this.closest("#shopify-section-header");

    if (this.header) {
      this.shadow = this.header.querySelector(".header-shadow");
      this.inverse = !!this.header.classList.contains("header--inverse");

      // Extra events
      document.addEventListener("keyup", (event) => {
        this.onKeyUp(event);
      });

      this.shadow?.addEventListener("click", (event) => {
        this.remove(event);
      });
    }
  }

  events() {
    if (!this.header) {
      super.events();
    }
  }

  onKeyUp(event) {
    if (!document.body.classList.contains("header-is-open")) {
      return;
    }
    if (event?.code.toUpperCase() !== "ESCAPE") {
      return;
    }
    const { toggleClass } = this.options;
    if (this.target?.classList.contains(toggleClass)) {
      this.remove();
    }
  }

  add() {
    super.add();
    trapFocus(this.header);
  }

  remove() {
    super.remove();
    this.closeAllSibblings();
    removeTrapFocus();
  }

  closeAllSibblings() {
    const collapsibles = this.target.querySelector("collapsible-element");
    collapsibles.closeAll();
  }
}

class ToggleSearch extends ToggleElementClass {
  constructor() {
    super();
  }

  construct() {
    super.construct();

    // elements
    this.header = this.closest("[data-header]");

    // options
    const { toggleClass, target } = this.options;

    // key up escape
    document.addEventListener("keyup", (event) => {
      this.onKeyUp(event);
    });

    // Remove classes incase the header navigation drawer is open.
    this.addEventListener("click", () => {
      document.body.classList.remove("header-is-open");
      document.body.classList.remove("menu-drawer-is-open");
      // Focus on input element if provided
      if (this.options.focusTarget) {
        document.querySelector(this.options.focusTarget)?.focus();
      }
    });
  }

  onKeyUp(event) {
    if (!document.body.classList.contains("header-search-is-open")) {
      return;
    }
    if (event?.code.toUpperCase() !== "ESCAPE") {
      return;
    }
    const { toggleClass } = this.options;
    if (this.target?.classList.contains(toggleClass)) {
      this.remove();
    }
  }

  add() {
    super.add();
    if (this.header) {
      document.body.classList.add("header-search-is-open");
    }
    if (this.target) {
      trapFocus(this.target);
    }
  }

  remove() {
    super.remove();
    if (this.header) {
      document.body.classList.remove("header-search-is-open");
    }
    removeTrapFocus();
  }
}

class StickyHeader extends HTMLElement {
  constructor() {
    super();
    this.handleScroll();
    this.body = document.querySelector("body");
    this.announcementBar = document.querySelector("announcement-bar");
    window.addEventListener("scroll", () => {
      this.handleScroll();
    });
  }

  /**
   * handleScroll
   * @description Handle the scroll state of the header.
   */
  handleScroll() {
    const pageYOffset = window.pageYOffset;
    const announcementBarHeight = this.announcementBar
      ? this.announcementBar.offsetHeight
      : 0;
    if (pageYOffset > 0) {
      this.classList.add("is-scrolled");
    } else {
      this.classList.remove("is-scrolled");
    }
    if (pageYOffset > announcementBarHeight) {
      this.body && this.body.classList.add("has-sticky-header");
    } else {
      this.body && this.body.classList.remove("has-sticky-header");
    }
  }
}

if (!customElements.get("collapsible-header")) {
  customElements.define("collapsible-header", CollapsibleHeader);
}

if (!customElements.get('collapsible-drawer')) {
  customElements.define('collapsible-drawer', CollapsibleDrawer);
}

if (!customElements.get("toggle-mobile-navigation")) {
  customElements.define("toggle-mobile-navigation", ToggleMobileNavigation);
}

if (!customElements.get("toggle-search")) {
  customElements.define("toggle-search", ToggleSearch);
}

if (!customElements.get("sticky-header")) {
  customElements.define("sticky-header", StickyHeader);
}
